import { Vector3, type Camera } from 'three'

/**
 * `TutorialCanvas` — the tapping hand on level 1.
 *
 * 🔍 `TutorialController$$OnLevelLoaded__0x20d27d8.c` →
 * `<CheckAndShowTutorial>d__5$$MoveNext__0x20d2a20.c`, and
 * `TutorialCanvas$$ShowTutorial__0x2143f9c.c`, `$$PlayTapAnimationContinuously__0x2144114.c`,
 * `$$FollowTarget__0x21444c8.c`, `$$HideTutorial__0x21443b4.c`.
 *
 * ## When it runs
 *
 *     OnLevelLoaded  -> CheckAndShowTutorial()
 *     CheckAndShowTutorial:
 *         destroy the old canvas if there is one;
 *         if (_levelService._userLevel != 1) return;          // :86
 *         await UniTask.DelayFrame(1);
 *         var controller = FindAnyObjectByType<BusController>();
 *         if (controller._boxes != null && controller._boxes.Count > 0)   // :177
 *             Instantiate(prefab).ShowTutorial(_boxes[i].transform, Camera.main);
 *
 * ❓ The index: Ghidra prints `get_Item(_boxes, 2, ...)`, but the guard right
 * above it is `Count > 0`, which only makes `[0]` safe — a `[2]` would have been
 * guarded `2 < Count`. The port takes the first box and says so here rather than
 * quietly picking one.
 *
 * `TutorialController.OnBusClicked` (`__0x20d291c.c`) is the end: on the first
 * `BusClickedEvent` it calls `HideTutorial()` and destroys the canvas, so the
 * hand lives exactly until the player taps something.
 *
 * ## The hand
 *
 * `TutorialCanvas.prefab` kept all four `RectTransform`s:
 *
 *     HandParent     256 x 256, centre-anchored, scale 0.85
 *     HandAnimation  256 x 256, centre-anchored, scale 1
 *     HandHold       stretched to HandAnimation, offset (110.5, -116.3)
 *     HandRelease    stretched to HandAnimation, offset (112.2, -119.7)
 *
 * The two offsets are why the *fingertip* lands on the bus and the rest of the
 * hand hangs down-right of it.
 *
 * `PlayTapAnimationContinuously` is one looping sequence:
 *
 *     handAnimationRect.localScale = Vector3.one;
 *     seq.AppendCallback(hold on, release off)
 *        .Append(DOScale(one * 0.8f, 0.15f))
 *        .Append(DOScale(one,        0.15f))
 *        .AppendCallback(release on, hold off)
 *        .AppendInterval(0.5f)
 *        .SetLoops(-1);
 *
 * and `FollowTarget` runs every frame:
 *
 *     var screen = _referenceCamera.WorldToScreenPoint(_targetTransform.position);
 *     handAnimationRect.position =
 *         Vector3.Lerp(handAnimationRect.position, screen + targetOffset,
 *                      Time.deltaTime * followSpeed);
 *
 * `followSpeed` is not a mystery: the class constructor writes `0x41200000` into
 * `0x38`, i.e. **10** (🔍 `TutorialCanvas$$.ctor__0x214471c.c`). `targetOffset`
 * (0x3C) is a public field the ctor leaves at zero; the prefab could override it
 * and that override is stripped, so ❓ — zero is the class's own value.
 */

/** `0x3e19999a` — each half of the squash. */
const TAP_SECONDS = 0.15
/** `0x3f000000` — the pause between taps. */
const REST_SECONDS = 0.5
const TAP_SCALE = 0.8
/** `TutorialCanvas$$.ctor` writes 10 into `followSpeed`. */
const FOLLOW_SPEED = 10

/** From the prefab, in CanvasScaler units. */
const HAND_SIZE = 256
const PARENT_SCALE = 0.85
/**
 * ❓ The canvas the hand hangs off has a `CanvasScaler`, but AssetRipper stripped
 * every one of its serialized fields (`TutorialCanvas.prefab`:135 — the
 * MonoBehaviour is CanvasScaler, guid `6dfc8ec6…`, and `m_ReferenceResolution`
 * is simply not there). So `--u`'s 1280-unit reference, which the HUD is fitted
 * to, is a guess for *this* canvas: at 1280 the 256x256 hand at scale 0.85 draws
 * 17% of the screen height, far bigger than the clip's UI reads.
 * Anchored instead to `ConstantPixelSize`, the mode a scaler-less canvas falls
 * back to: 256 * 0.85 = 217.6 *screen* px, which on a 1080x2400 phone is 9.07%
 * of the height — i.e. the same hand at a 2400-unit reference. Hence 1280/2400.
 */
const HAND_REFERENCE_HEIGHT = 2400
const HOLD_OFFSET = { x: 110.5, y: -116.3 }
const RELEASE_OFFSET = { x: 112.2, y: -119.7 }

const _world = new Vector3()

/** One cycle of `PlayTapAnimationContinuously`: tap down, tap up, then rest. */
export const TAP_CYCLE_SECONDS = TAP_SECONDS * 2 + REST_SECONDS

/**
 * Where the looping tap sequence is at `clock` seconds in.
 *
 * Pure so it can be checked against the decomp's own ordering: the first
 * callback (hold on, release off) runs **before** the squash, the second
 * (release on, hold off) **after** it and before the half-second rest — so the
 * released hand is what is on screen for most of the cycle.
 */
export function tapPhase(clock: number): { scale: number; holding: boolean } {
  const t = ((clock % TAP_CYCLE_SECONDS) + TAP_CYCLE_SECONDS) % TAP_CYCLE_SECONDS
  if (t < TAP_SECONDS) {
    return { holding: true, scale: 1 + (TAP_SCALE - 1) * (t / TAP_SECONDS) }
  }
  if (t < TAP_SECONDS * 2) {
    return { holding: true, scale: TAP_SCALE + (1 - TAP_SCALE) * ((t - TAP_SECONDS) / TAP_SECONDS) }
  }
  return { holding: false, scale: 1 }
}

/**
 * `FollowTarget`'s one line, as a scalar.
 *
 *     position = Vector3.Lerp(position, screen, Time.deltaTime * followSpeed)
 *
 * `Vector3.Lerp` clamps its `t`, which matters at low frame rates: at 60 Hz the
 * factor is 1/6, at 5 Hz it would be 2 and the hand would overshoot without the
 * clamp.
 */
export function followStep(current: number, target: number, dt: number): number {
  return current + (target - current) * Math.min(1, Math.max(0, dt * FOLLOW_SPEED))
}

/**
 * The point `CheckAndShowTutorial` hands to `ShowTutorial`.
 *
 * 🔍 It passes `_boxes[0].transform.position` — the box's **own** transform, in
 * the scene's space — and `FollowTarget` projects that with `WorldToScreenPoint`.
 *
 * ⚠️ This exists so the caller cannot convert twice. `Bus.position` is already
 * in the port's space: `Bus`'s constructor runs `convertPosition(spec.position)`
 * once (`bus.ts`:308). `main.ts` used to run it again, and `convertPosition` is
 * `(x, y, -z)` — an involution, so the second call mirrored the target back to
 * the Unity z and the hand pointed at empty asphalt. See `tutorialHand.test.ts`.
 */
export function tutorialTarget(bus: { position: Vector3 }, out = new Vector3()): Vector3 {
  return out.copy(bus.position)
}

export class TutorialCanvas {
  private readonly parent: HTMLElement
  private readonly animation: HTMLElement
  private readonly hold: HTMLImageElement
  private readonly release: HTMLImageElement

  /** `_targetTransform` — null when the canvas is not instantiated. */
  private target: (() => Vector3) | null = null
  /** Seconds into the looping sequence. */
  private clock = 0
  /** Screen position in stage pixels; `FollowTarget` lerps towards the target. */
  private readonly at = { x: 0, y: 0 }
  private placed = false

  constructor(root: HTMLElement) {
    this.parent = document.createElement('div')
    this.parent.id = 'tutorialHand'
    this.parent.hidden = true

    this.animation = document.createElement('div')
    this.animation.className = 'handAnimation'
    this.parent.append(this.animation)

    this.hold = document.createElement('img')
    this.hold.className = 'handSprite'
    this.hold.src = 'assets/ui/Hand_Hold.png'
    this.hold.alt = ''
    this.hold.style.setProperty('--dx', `${HOLD_OFFSET.x}`)
    this.hold.style.setProperty('--dy', `${HOLD_OFFSET.y}`)

    this.release = document.createElement('img')
    this.release.className = 'handSprite'
    this.release.src = 'assets/ui/Hand_Release.png'
    this.release.alt = ''
    this.release.style.setProperty('--dx', `${RELEASE_OFFSET.x}`)
    this.release.style.setProperty('--dy', `${RELEASE_OFFSET.y}`)

    this.animation.append(this.hold, this.release)
    this.parent.style.setProperty('--hand', `${HAND_SIZE}`)
    this.parent.style.setProperty(
      '--handScale',
      `${(PARENT_SCALE * 1280) / HAND_REFERENCE_HEIGHT}`,
    )
    root.append(this.parent)
  }

  /**
   * `TutorialCanvas.ShowTutorial(target, camera)`. The caller decides whether
   * the level qualifies — see `CheckAndShowTutorial`'s `_userLevel != 1` gate.
   */
  show(target: () => Vector3): void {
    this.target = target
    this.clock = 0
    this.placed = false
    this.parent.hidden = false
  }

  /** `HideTutorial()`, and then `Destroy(canvas)` — one and the same here. */
  hide(): void {
    this.target = null
    this.parent.hidden = true
  }

  get visible(): boolean {
    return this.target !== null
  }

  /** `Update` → `FollowTarget`, plus the frame of the looping tap sequence. */
  sync(camera: Camera, dt: number, stage: DOMRect): void {
    if (!this.target) return

    _world.copy(this.target()).project(camera)
    const screenX = ((_world.x + 1) / 2) * stage.width
    const screenY = ((1 - _world.y) / 2) * stage.height

    if (!this.placed) {
      // Nothing in the decomp seeds the rect, but a first frame that lerps from
      // wherever the prefab was authored would fly the hand across the screen.
      this.at.x = screenX
      this.at.y = screenY
      this.placed = true
    } else {
      this.at.x = followStep(this.at.x, screenX, dt)
      this.at.y = followStep(this.at.y, screenY, dt)
    }
    this.parent.style.left = `${this.at.x}px`
    this.parent.style.top = `${this.at.y}px`

    this.clock = (this.clock + dt) % TAP_CYCLE_SECONDS
    const { scale, holding } = tapPhase(this.clock)
    this.animation.style.transform = `scale(${scale})`
    this.hold.hidden = !holding
    this.release.hidden = holding
  }
}
